import { useEffect, useState } from "react";
import axios from "axios";
import UserStatus from "./UserStatus";
import editIcon from "/assets/icon/Edit.png";
import ArrowDownIcon from "/assets/ArrowDown.svg";
import ArrowUpIcon from "/assets/ArrowUp.svg";
import Delete from "/assets/icon/delete.png";
import ToggleSwitch from "./ToggleSwitch";
import correct from "/correct.png";

function DetailUser({ userId, onClose, openIsEditing, onSuccess }) {
  const [userData, setUserData] = useState({ branches: [] });
  const [refreshCount, setRefreshCount] = useState(0);
  const [completionMessageVisible, setCompletionMessageVisible] =
    useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/user-permissions/${userId}`
        );
        const fetchedData = response.data;
        setUserData(fetchedData);
        setEditedData({
          name: fetchedData.user_name || "",
          phone_no: fetchedData.phone_no || "",
          email: fetchedData.email || "",
          isActive: fetchedData.isActive || false,
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserData({});
        setEditedData({
          name: "",
          phone_no: "",
          email: "",
          isActive: false,
        });
      }
    };

    fetchUserData();
  }, [userId, refreshCount]);

  const refreshData = () => {
    setRefreshCount((prev) => prev + 1);
  };

  const [editedData, setEditedData] = useState({
    name: "",
    phone_no: "",
    email: "",
    isActive: false,
  });

  const [isEditing, setIsEditing] = useState(openIsEditing);

  useEffect(() => {
    setIsEditing(openIsEditing); // Optionally sync with external prop
  }, [openIsEditing]);

  const checkEmailExistence = async (email) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/check-email2`,
        {
          email,
        }
      );
      return response.data.exists; // ส่งค่า true หรือ false กลับมา
    } catch (error) {
      console.error("Error checking email existence:", error);
      return false; // หากเกิดข้อผิดพลาดในการเรียก API
    }
  };
  const [existenceMessage, setExistenceMessage] = useState("");
  const [existenceMessageColor, setExistenceMessageColor] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true); // Initially assume the email is valid

  const isValidEmail = (email) => {
    // ใช้ regular expression เพื่อตรวจสอบรูปแบบของอีเมล
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email); // คืนค่า true ถ้าอีเมลถูกต้องตามรูปแบบ, ไม่则อคืนค่า false
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "email") {
      if (!value) {
        setExistenceMessage("กรุณากรอก email");
        setExistenceMessageColor("red");
        setIsEmailValid(false);
        return;
      }

      const validEmail = isValidEmail(value);
      setIsEmailValid(validEmail); // Update the validity state based on the regex check

      if (!validEmail) {
        setExistenceMessage("อีเมลไม่ถูกต้อง");
        setExistenceMessageColor("red");
        return;
      }

      const exists = await checkEmailExistence(value);
      setIsEmailValid(!exists); // Email must not exist to be valid

      if (exists) {
        setExistenceMessage(`มี ${value} แล้วในระบบ`);
        setExistenceMessageColor("red");
      } else {
        setExistenceMessage(`สามารถใช้อีเมลนี้ได้`);
        setExistenceMessageColor("#3DCC85");
      }
    }
  };

  const [showPermissions, setShowPermissions] = useState({});

  const togglePermissions = (branchId) => {
    setShowPermissions((prevShowPermissions) => ({
      ...prevShowPermissions,
      [branchId]: !prevShowPermissions[branchId],
    }));
  };

  const [permissions, setPermissions] = useState({});

  const togglePermission = (branchId, page, currentAccess) => {
    setPermissions((prev) => ({
      ...prev,
      [branchId]: {
        ...prev[branchId],
        [page]: !currentAccess,
      },
    }));
  };

  const handleBrightnessChange = (branchId, newBrightness) => {
    console.log(
      `Changing brightness for branch ${branchId} to ${newBrightness}`
    );
    setPermissions((prev) => {
      const updatedPermissions = {
        ...prev,
        [branchId]: {
          ...prev[branchId],
          controllability: newBrightness,
        },
      };

      return updatedPermissions;
    });
  };

  const saveAllPermissions = async () => {
    const promises = Object.entries(permissions).map(
      ([branchId, currentPerms]) => {
        const originalPerms =
          userData.branches.find((b) => b.branch_id === branchId)
            ?.permissions || {};
        const controllability =
          userData.branches.find((b) => b.branch_id === branchId)
            ?.controllabilities?.light_bulb || 1;
        const newControllability =
          permissions[branchId]?.controllability || controllability;

        // Construct newPerms object
        let newPerms = {};
        let changesDetected = false;
        for (const [key, value] of Object.entries(currentPerms)) {
          if (value !== originalPerms[key]) {
            newPerms[key] = value;
            changesDetected = true;
          }
        }

        // Remove controllability from newPerms
        if (newPerms.hasOwnProperty("controllability")) {
          delete newPerms["controllability"];
        }

        // Include controllability changes
        if (newControllability !== controllability) {
          changesDetected = true;
        }

        // If no changes, skip the API call for this branch
        if (!changesDetected) {
          console.log(
            `No changes detected for branch ${branchId}, skipping update.`
          );
          return Promise.resolve({ success: true, branchId, skipped: true });
        }

        // Construct the payload
        const payload = {
          newPerms,
          light_bulb_control: newControllability,
        };

        // Log the payload
        console.log(`Payload for branch ${branchId}:`, payload);

        // Send only the changed permissions to the API
        return axios
          .put(
            `${
              import.meta.env.VITE_API_URL
            }/api/updateUserPermissions/${userId}/${branchId}`,
            payload
          )
          .then((response) => ({
            success: true,
            branchId,
            response: response.data,
          }))
          .catch((error) => ({
            success: false,
            branchId,
            error: error.response ? error.response.data : "Unknown error",
          }));
      }
    );

    try {
      const results = await Promise.all(promises);
      const errors = results.filter(
        (result) => !result.success && !result.skipped
      );

      if (errors.length > 0) {
        console.error("Failed to update permissions for branches:", errors);
        console.log(
          `Some permissions updates failed for ${errors.length} branches. Check the console for more details.`
        );
      } else {
        console.log("All applicable permissions updated successfully.");
      }
    } catch (error) {
      console.error("Error updating permissions:", error);
      console.log("Failed to update all permissions due to an error");
    }
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    console.log("Edit icon clicked");
  };

  const handleCancelEdit = async () => {
    try {
      // Directly retrieve and use the branch IDs from sessionStorage for deletion requests
      const branchIds =
        JSON.parse(sessionStorage.getItem("selectedBranchRecod")) || [];

      const deleteRequests = branchIds.map((branchId) =>
        axios.delete(
          `${import.meta.env.VITE_API_URL}/users/${userId}/branches/${branchId}`
        )
      );

      await Promise.all(deleteRequests);
      console.log("All selected branches have been deleted.");

      // Remove the selectedBranchRecod from sessionStorage completely
      sessionStorage.removeItem("selectedBranchRecod");

      setDeletionRecords([]); // Clear the records after successful deletion
      onClose();
      onSuccess();
    } catch (error) {
      console.error("Error during deletion:", error);
    }
  };

  const [showAddBranchModal, setShowAddBranchModal] = useState(false);

  const toggleAddBranchModal = () => {
    setShowAddBranchModal(!showAddBranchModal);
  };

  const [branchDropdownOpen, setBranchDropdownOpen] = useState(false);

  const [selectedBranch, setSelectedBranch] = useState("");
  const handleBranchSelect = (branch) => {
    setSelectedBranch(branch);
    setBranchDropdownOpen(false);
    sessionStorage.setItem("selectedBranch", branch.id);

    // Retrieve the existing record array from sessionStorage
    let recordArray =
      JSON.parse(sessionStorage.getItem("selectedBranchRecod")) || [];
    // Add the new branch ID to the array
    recordArray.push(branch.id);
    // Save the updated array back to sessionStorage
    sessionStorage.setItem("selectedBranchRecod", JSON.stringify(recordArray));
  };

  const toggleBranchDropdown = () => {
    setBranchDropdownOpen(!branchDropdownOpen);
  };

  const [unauthorizedBranches, setUnauthorizedBranches] = useState([]);
  const [forceUpdate, setForceUpdate] = useState(false); // Changed to forceUpdate

  useEffect(() => {
    axios
      .get(
        `${import.meta.env.VITE_API_URL}/user/${userId}/unauthorized-branches`
      )
      .then((response) => {
        setUnauthorizedBranches(response.data);
      })
      .catch((error) => {
        console.error("Error fetching unauthorized branches:", error);
        setUnauthorizedBranches([]);
      });
  }, [userId, forceUpdate]);
  function Modal({ isOpen, onClose, children, onAdSuccess }) {
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [permissions, setPermissions] = useState({
      page_alert: false,
      page_weather: false,
      page_lighting: false,
      page_gis: false,
      page_camera: false,
      page_display: false,
    });
    if (!isOpen) return null;
    const toggleAllPermissions = (value) => {
      setPermissions((prev) =>
        Object.keys(prev).reduce(
          (acc, key) => ({
            ...acc,
            [key]: value, // Set all permissions to the specified value
          }),
          {}
        )
      );
    };
    const togglePermission = (page) => {
      setPermissions((prev) => ({
        ...prev,
        [page]: !prev[page], // Toggle the boolean value
      }));
    };
    const allSelected = Object.values(permissions).every(Boolean);

    const selectAllStyle = {
      backgroundColor: allSelected ? "#2762f8" : "transparent", // Blue if all are true, transparent otherwise
      color: allSelected ? "white" : "#D0D5DD", // White text if all are true, grey if not
      cursor: "pointer",
      padding: "0.3rem 0.8rem",
    };

    if (!isOpen) return null;

    const [lightBulbControl, setLightBulbControl] = useState(2);

    const handleLightBulbChange = (newBrightness) => {
      setLightBulbControl(newBrightness);
    };

    const handleSavePermissions = async () => {
      if (!selectedBranch) {
        console.error("No branch selected");
        return;
      }

      // Set 'page_dashboard' to a default value if it's not already set in permissions
      const defaultPermissions = {
        page_dashboard: true, // Default value for 'page_dashboard'
        ...permissions,
      };

      const payload = {
        userId: userId,
        branchId: selectedBranch.id,
        permissions: defaultPermissions,
        light_bulb_control: lightBulbControl,
      };

      console.log("Sending payload:", payload); // Log the payload to debug

      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/assign-user-to-branch`,
          payload
        );

        console.log("Permissions saved:", response.data);
        onSuccess(); // Call the onSuccess callback if provided
        onClose();
        onAdSuccess();
        setForceUpdate(!forceUpdate);
      } catch (error) {
        console.error(
          "Error saving permissions:",
          error.response ? error.response.data : error
        );
      }
    };

    return (
      <div className="modal-background-fixedmodel">
        <div className="add-branch-modal" onClick={(e) => e.stopPropagation()}>
          <h2>เพิ่มสิทธิเข้าถึงสาขา</h2>
          <div
            className={`dropdownUser ${branchDropdownOpen ? "clicked" : ""}`}
            onClick={toggleBranchDropdown}
          >
            {!selectedBranch || !selectedBranch.name
              ? "เลือกสาขาที่ต้องการเพิ่มสิทธิ"
              : selectedBranch.name}

            <img
              className={`arrow-icon-branch ${
                branchDropdownOpen ? "rotate" : ""
              }`}
              src={ArrowDownIcon}
              alt="Arrow"
            />

            {branchDropdownOpen && (
              <div className="dropdown-content-adduser">
                {unauthorizedBranches.map((branch) => (
                  <div
                    key={branch.id}
                    onClick={() => handleBranchSelect(branch)}
                  >
                    <li>{branch.name}</li>
                  </div>
                ))}
              </div>
            )}
          </div>
          <h2>สิทธิในการควบคุม</h2>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="brightness"
                value="ดูเท่านั้น"
                checked={lightBulbControl === 2}
                onChange={() => handleLightBulbChange(2)}
              />
              ดูเท่านั้น
            </label>
            <label>
              <input
                type="radio"
                name="brightness"
                value="ปรับค่าสว่างไฟได้"
                checked={lightBulbControl === 3}
                onChange={() => handleLightBulbChange(3)}
              />
              ปรับค่าสว่างไฟได้
            </label>
          </div>
          <h2>เลือกสิทธิการเข้าถึงหน้าต่าง ๆ</h2>
          <div className="wrapperShowRelative">
            <ul>
              <li
                className="first-item"
                style={selectAllStyle}
                onClick={() => toggleAllPermissions(!allSelected)}
              >
                {allSelected && <span> ✓</span>} เลือกทั้งหมด
                {/* Checkmark if all permissions are true */}
              </li>
              {Object.entries(permissions).map(([page, access]) => {
                let pageTitle =
                  {
                    page_alert: "การแจ้งเตือน",
                    page_weather: "สภาพอากาศ",
                    page_lighting: "ควบคุมไฟ",
                    page_gis: "แผนที่",
                    page_camera: "กล้อง CCTV",
                    page_display: "จอ LED",
                  }[page] || page;

                const listItemStyle = {
                  backgroundColor: access ? "#2762f8" : "transparent",
                  color: access ? "white" : "#D0D5DD",
                  cursor: "pointer",
                  padding: "0.3rem 0.8rem",
                };

                return (
                  <li
                    key={page}
                    style={listItemStyle}
                    onClick={() => togglePermission(page)}
                  >
                    {access ? "✓" : "+"} {pageTitle}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="button-adduser-detali">
            <button
              className="cancel-button"
              onClick={() => {
                sessionStorage.setItem("selectedBranch", "");
                onClose();
              }}
            >
              ยกเลิก
            </button>

            <button className="confirm-button" onClick={handleSavePermissions}>
              บันทึก
            </button>
          </div>

          {/* {children} */}
        </div>
      </div>
    );
  }

  if (!userData) {
    return <div>Error loading user data. Please try again later.</div>;
  }

  const renderFloatLabelInput = (
    label,
    type,
    name,
    id,
    value,
    onChange,
    isEmailValid,
    blink
  ) => {
    // Apply 'red-border' always if the email is invalid
    let inputClass = `input-required ${!isEmailValid ? "red-border" : ""}`;

    // Add 'blink' class only during a blink state when the email is invalid
    if (blink && !isEmailValid) {
      inputClass += " blink";
    }

    return (
      <div className="float-label-container">
        <input
          type={type}
          id={id}
          name={name}
          value={value || ""}
          onChange={onChange}
          className={inputClass}
          required={true}
          placeholder=" "
        />
        <label htmlFor={id}>
          {label} <span className="required-asterisk"> *</span>
        </label>
      </div>
    );
  };

  const [removingBranches, setRemovingBranches] = useState([]);
  const [deletionRecords, setDeletionRecords] = useState([]);

  const handleDeleteBranch = (branchId) => {
    console.log(
      "Preparing to delete branch with ID:",
      branchId,
      "for user ID:",
      userId
    );
    setRemovingBranches((prev) => [...prev, branchId]);

    // Store the deletion record
    setDeletionRecords((prev) => [...prev, { userId, branchId }]);

    // Proceed to visually remove the branch (this could also be postponed until actual confirmation)
    setTimeout(() => {
      const remainingBranches = userData.branches.filter(
        (branch) => branch.branch_id !== branchId
      );
      setUserData({ ...userData, branches: remainingBranches });
      setRemovingBranches((prev) => prev.filter((id) => id !== branchId));
    }, 300); // Duration to match any animations or delay requirements
  };

  const [blink, setBlink] = useState(false);

  const confirmDeletions = async () => {
    if (!isEmailValid) {
      setBlink(true); // Start blinking
      setTimeout(() => setBlink(false), 1000); // Stop blinking after 3 seconds
      return; // Stop further execution if the email is invalid
    }

    try {
      // Prepare edited user data for updating
      const editedUserData = {
        name: editedData.name,
        phone_no: editedData.phone_no,
        email: editedData.email,
        isActive: editedData.isActive, // Ensure all relevant fields are included
      };

      // Send the request to update user data
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/users/${userId}`, // Ensure correct usage of userId
        editedUserData
      );
      console.log("User data updated successfully");

      // After successful user update, proceed to delete branches
      const deleteRequests = deletionRecords.map((record) =>
        axios.delete(
          `${import.meta.env.VITE_API_URL}/users/${record.userId}/branches/${
            record.branchId
          }`
        )
      );
      await Promise.all(deleteRequests);
      console.log("All selected branches have been deleted.");
      setDeletionRecords([]); // Clear the records after successful deletion

      onSuccess();
    } catch (error) {
      console.error("Error during the update or delete operations:", error);
    }
  };

  if (isEditing) {
    return (
      <div className="modal-background-fixedmodel">
        {completionMessageVisible && (
          <div id="completionMessage">
            <div className={`alert-container alert-success`}>
              <button className="close-button">&times;</button>
              {`แก้ไขข้อมูลผู้ใช้งานของ ${userData.user_name} แล้ว`}
              <div>
                <img src={correct} alt="Success" />
              </div>
            </div>
          </div>
        )}
        <div id="userDetail" className="userDetail">
          <div className="mainedit">
            <div className="edit-form">
              <h3>
                แก้ไขข้อมูลผู้ใช้งาน
                <span style={{ color: "#f2274c" }}> **</span>
              </h3>
              <p></p>
              {renderFloatLabelInput(
                "ชื่อ", // ตัวอย่าง label
                "text", // ประเภทของ input
                "name", // ชื่อของ input
                "nameInput", // ID ของ input
                editedData.name, // ค่าของ input
                handleInputChange // function ที่จะเรียกเมื่อมีการเปลี่ยนแปลงค่า input
              )}

              <p></p>
              {renderFloatLabelInput(
                "หมายเลขโทรศัพท์", // label ของ input
                "text", // ประเภทของ input
                "phone_no", // ชื่อของ input ต้องแตกต่างจากชื่อของ input อื่น
                "phoneNoInput", // ID ของ input ต้องแตกต่างกัน
                editedData.phone_no, // ค่าของ input
                handleInputChange // function ที่จะเรียกเมื่อมีการเปลี่ยนแปลงค่า input
              )}
              <div className="reltiveCheckEmail">
                {" "}
                {renderFloatLabelInput(
                  "อีเมล",
                  "text",
                  "email",
                  "emailInput",
                  editedData.email,
                  handleInputChange,
                  isEmailValid,
                  blink
                )}
                {existenceMessage && (
                  <label
                    style={{
                      color: existenceMessageColor
                        ? existenceMessageColor
                        : "#3DCC85",
                    }}
                  >
                    {existenceMessage}
                  </label>
                )}
              </div>
            </div>
            <div className="toggle-switch-container">
              {/* <span style={{ fontSize: "14px" }}>เปิดสิทธิให้กับผู้ใช้งาน</span>
              <ToggleSwitch /> */}
            </div>
            <span>
              สาขาที่เข้าถึงได้{" "}
              <span style={{ color: "#7DA1FB", fontWeight: "bold" }}>
                {userData.branches ? userData.branches.length : 0}
              </span>{" "}
              สาขา
            </span>
            <h5 onClick={toggleAddBranchModal}>เพิ่มสิทธิสาขา</h5>

            <div className="branch-container">
              {userData.branches && userData.branches.length > 0 ? (
                userData.branches.map((branch) => (
                  <div
                    key={branch.branch_id}
                    className={`branch-item-edit ${
                      removingBranches.includes(branch.branch_id)
                        ? "fade-out-left"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePermissions(branch.branch_id);
                    }}
                  >
                    {" "}
                    <p>ชื่อสาขา</p>
                    <p>รหัสสาขา</p>
                    <p>สิทธิควบคุมไฟ</p>
                    <strong>{branch.branch_name}</strong>
                    <strong>{branch.code}</strong>
                    <div className="radio-group">
                      <label>
                        <input
                          type="radio"
                          name={`brightness-${branch.branch_id}`}
                          value="ดูเท่านั้น"
                          checked={
                            permissions[branch.branch_id]?.controllability ===
                              2 ||
                            (!permissions[branch.branch_id]?.controllability &&
                              branch.controllabilities?.light_bulb === 2)
                          }
                          onChange={() =>
                            handleBrightnessChange(branch.branch_id, 2)
                          }
                        />
                        ดูเท่านั้น
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`brightness-${branch.branch_id}`}
                          value="ปรับค่าสว่างไฟได้"
                          checked={
                            permissions[branch.branch_id]?.controllability ===
                              3 ||
                            (!permissions[branch.branch_id]?.controllability &&
                              branch.controllabilities?.light_bulb === 3)
                          }
                          onChange={() =>
                            handleBrightnessChange(branch.branch_id, 3)
                          }
                        />
                        ปรับค่าสว่างไฟได้
                      </label>
                    </div>
                    <div className="detailPermission">
                      <p>สิทธิการเข้าถึงข้อมูล</p>
                      <div className="wrapperShow">
                        <ul>
                          {branch.permissions ? (
                            Object.entries(branch.permissions).map(
                              ([page, access]) => {
                                if (
                                  page === "page_dashboard" ||
                                  page === "page_alert"
                                ) {
                                  return null;
                                }

                                let pageTitle;
                                switch (page) {
                                  case "page_weather":
                                    pageTitle = "สภาพอากาศ";
                                    break;
                                  case "page_lighting":
                                    pageTitle = "ควบคุมไฟ";
                                    break;
                                  case "page_gis":
                                    pageTitle = "แผนที่";
                                    break;
                                  case "page_camera":
                                    pageTitle = "กล้อง CCTV";
                                    break;
                                  case "page_display":
                                    pageTitle = "จอ LED";
                                    break;
                                  default:
                                    pageTitle = page;
                                }

                                const currentAccess =
                                  permissions[branch.branch_id]?.[page] ??
                                  access;
                                const listItemStyle = {
                                  border: "1px solid #bed0fd",
                                  backgroundColor: currentAccess
                                    ? "#2762f8"
                                    : "transparent",
                                  borderRadius: "2rem",
                                  listStyle: "none",
                                  padding: "0.3rem 0.8rem",
                                  width: "100px",
                                  textAlign: "center",
                                  cursor: "pointer",
                                  color: currentAccess ? "white" : "#D0D5DD",
                                };

                                const displayTitle = currentAccess
                                  ? `✓ ${pageTitle}`
                                  : `+ ${pageTitle}`;

                                return (
                                  <li
                                    key={page}
                                    style={listItemStyle}
                                    onClick={() =>
                                      togglePermission(
                                        branch.branch_id,
                                        page,
                                        currentAccess
                                      )
                                    }
                                  >
                                    {displayTitle}
                                  </li>
                                );
                              }
                            )
                          ) : (
                            <p>No permissions available.</p>
                          )}
                        </ul>
                      </div>
                    </div>
                    <img
                      src={Delete}
                      alt="delete"
                      id="Delete"
                      onClick={() => handleDeleteBranch(branch.branch_id)}
                    />
                  </div>
                ))
              ) : (
                <p>ไม่มีสาขาให้แก้ไข</p>
              )}
            </div>

            <div className="button-adduser-detali">
              <button className="cancel-button" onClick={handleCancelEdit}>
                ยกเลิก
              </button>

              <button
                className="confirm-button"
                onClick={async () => {
                  await confirmDeletions();
                  await saveAllPermissions();
                  sessionStorage.removeItem("selectedBranchRecod");
                  // รอ 3 วินาทีก่อนที่จะปิดโมดัลไดอะล็อก
                  setTimeout(() => {
                    onClose(); // ให้ปิดโมดัลไดอะล็อก
                  }, 2000); // 2 วินาที
                  setCompletionMessageVisible(true);
                  setTimeout(() => setCompletionMessageVisible(false), 2000); // Hide after 2 seconds
                  document.getElementById("userDetail").style.display = "none";
                  onSuccess();
                }}
                // เปลี่ยนการแสดง alert เป็น div
              >
                บันทึก
              </button>
            </div>
          </div>

          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <Modal
          isOpen={showAddBranchModal}
          onClose={toggleAddBranchModal}
          onAdSuccess={refreshData}
        />
      </div>
    );
  }

  return (
    <div className="modal-background-fixedmodel">
      <div className="userDetail">
        {userData ? (
          <>
            <div className="nameDetailUser">
              <div className="nameDetailUserMain">
                <span>{userData.user_name}</span>
              </div>
              <div className="UserStatus">
                <UserStatus isOnline={userData.active} />
              </div>
              <img src={editIcon} alt="edit" onClick={toggleEditMode} />
            </div>

            <div className="nameDetailUser2">
              <div>
                <span>หมายเลขโทรศัพท์</span>
                <p>{userData.phone_no}</p>
              </div>
              <div>
                <span>อีเมล</span>
                <p>{userData.email}</p>
              </div>
            </div>
            <span>
              สาขาที่เข้าถึงได้{" "}
              <span style={{ color: "#7DA1FB", fontWeight: "bold" }}>
                {userData.branches ? userData.branches.length : 0}{" "}
              </span>
              สาขา
            </span>

            <div className="branch-container">
              {userData.branches && userData.branches.length > 0 ? (
                userData.branches.map((branch) => (
                  <div
                    key={branch.branch_id}
                    className="branch-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePermissions(branch.branch_id);
                    }}
                  >
                    <p>ชื่อสาขา</p>
                    <p>รหัสสาขา</p>
                    <p>สิทธิควบคุมไฟ</p>

                    <strong>{branch.branch_name}</strong>
                    <strong>{branch.code}</strong>
                    <strong>
                      {branch.controllabilities?.light_bulb === 1
                        ? "ค่าเริ่มต้น"
                        : branch.controllabilities?.light_bulb === 2
                        ? "ดูเท่านั้น"
                        : branch.controllabilities?.light_bulb === 3
                        ? "ปรับค่าสว่างได้"
                        : "ไม่มีสิทธิ์ควบคุม"}
                    </strong>

                    <img
                      src={ArrowDownIcon}
                      alt={
                        showPermissions[branch.branch_id]
                          ? "Collapse"
                          : "Expand"
                      }
                      className={`arrow-icon-user ${
                        showPermissions[branch.branch_id] ? "rotate" : ""
                      }`}
                    />

                    {showPermissions[branch.branch_id] && (
                      <div className="detailPermission">
                        <p>สิทธิการเข้าถึงข้อมูล</p>
                        <div className="wrapperShow">
                          <ul>
                            {branch.permissions ? (
                              Object.entries(branch.permissions).map(
                                ([page, access]) => {
                                  if (
                                    page === "page_dashboard" ||
                                    page === "page_alert"
                                  ) {
                                    return null; // Skip rendering for dashboard and alert pages
                                  }

                                  let pageTitle;
                                  switch (page) {
                                    case "page_weather":
                                      pageTitle = "สภาพอากาศ";
                                      break;
                                    case "page_lighting":
                                      pageTitle = "ควบคุมไฟ";
                                      break;
                                    case "page_gis":
                                      pageTitle = "แผนที่";
                                      break;
                                    case "page_camera":
                                      pageTitle = "กล้อง CCTV";
                                      break;
                                    case "page_display":
                                      pageTitle = "จอ LED";
                                      break;
                                    default:
                                      pageTitle = page; // Use the page key as the title if not one of the known types
                                  }

                                  return access ? (
                                    <li key={page}>{pageTitle}</li>
                                  ) : null;
                                }
                              )
                            ) : (
                              <li>
                                No permissions assigned or permissions data is
                                unavailable.
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p>ไม่มีสาขา</p>
              )}
            </div>

            <button className="close-button" onClick={onClose}>
              {" "}
              &times;{" "}
            </button>
          </>
        ) : (
          <p>Loading...</p>
        )}
      </div>
    </div>
  );
}

export default DetailUser;
